import { store } from "../store/store";
import { httpService } from "./http.service";

const BASE_URL = "task";

export const taskService = {
  getAll,
  getById,
  create,
  createTaskInGroup,
  update,
  deleteTask,
  addSubtask,
  removeSubtask,
  getSubTasksByParentId,
  duplicateTask,
  updateTaskLabels,
  updateTaskCmpLabel,
  deleteTaskCmpLabel,
  createTaskColumn,
  reorderTaskColumns,
  getTasksByGroup,
  getGroupTaskStats,
  moveTasksAnotherBoard,
  archivedSubtasks,
  getInfo,
  getTaskByAnalisys,
  getTaskMention,
  getTaskBpmnDiagram,
  saveTaskBpmnDiagram,
  listTaskDocuments,
  createTaskDocument,
  getTaskDocumentById,
  updateTaskDocumentById,
  deleteTaskDocumentById,
  createBelow,
  pauseStatusTimeTracking,
  createComment,
  deleteComment,
  updateComment,
  toggleLike,
  createAnswer,
  deleteAnswer,
  updateAnswer
};

function normalizeDocumentsResponse(res) {
  if (Array.isArray(res)) return res;

  if (Array.isArray(res?.documents)) return res.documents;
  if (Array.isArray(res?.items)) return res.items;

  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.documents)) return res.data.documents;

  return [];
}

export function buildDocumentCounts(documents = []) {
  const list = normalizeDocumentsResponse(documents);
  const byType = {};

  for (const doc of list) {
    const type = doc?.type || "unknown";
    byType[type] = (byType[type] || 0) + 1;
  }

  return {
    total: list.length,
    byType
  };
}

async function createTaskInGroup(groupId, accountId, taskData) {
  return await httpService.post(`${BASE_URL}/taskToGroup/${accountId}/${groupId}/`, taskData);
}

async function getAll() {
  return await httpService.get(BASE_URL);
}

async function getById(id) {
  return await httpService.get(`${BASE_URL}/${id}`);
}

async function getInfo(id) {
  return await httpService.get(`${BASE_URL}/info/${id}`);
}

async function getSubTasksByParentId(id) {
  return await httpService.get(`${BASE_URL}/${id}/subtasks`);
}

async function create(accountId, objTask, activityData, options = {}) {
  return await httpService.post(`${BASE_URL}/${accountId}`, {
    task: objTask,
    activityData: activityData,
    ...options,
  });
}

async function update(accountId, id, patch, activityData = null) {
  const boardId = store.getState().board.board?._id;
  return await httpService.put(`${BASE_URL}/${accountId}/${boardId}/${id}`, {
    patch,
    activityData: activityData || null,
  });
}

async function pauseStatusTimeTracking(accountId, taskId, automationId) {
  return await httpService.patch(`${BASE_URL}/${accountId}/${taskId}/status-time/${automationId}/pause`);
}

async function addSubtask(accountId, taskId, subtaskData, options = {}) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/subtasks`, {
    subtaskData,
    ...options,
  });
}

async function removeSubtask(accountId, parentTaskId, subtaskId, payload = {}) {
  return await httpService.patch(
    `${BASE_URL}/${accountId}/${parentTaskId}/subtasks/${subtaskId}/remove`,
    payload
  );
}

async function duplicateTask(taskId, accountId, objTask) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/duplicate`, objTask);
}

async function deleteTask(accountId, id) {
  return await httpService.delete(`${BASE_URL}/${accountId}/${id}`);
}

async function updateTaskLabels(accountId, taskId, payloadOrLabels, componentId = null) {
  let payload = payloadOrLabels;

  if (Array.isArray(payloadOrLabels)) {
    if (!componentId) throw new Error("updateTaskLabels: componentId é obrigatório quando labels é array.");
    payload = { labels: payloadOrLabels, componentId };
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("updateTaskLabels: payload inválido. Use { componentId, labels }.");
  }

  if (!payload.componentId) {
    throw new Error("updateTaskLabels: componentId é obrigatório.");
  }

  if (!Array.isArray(payload.labels)) {
    throw new Error("updateTaskLabels: labels deve ser um array.");
  }

  return await httpService.patch(`${BASE_URL}/${accountId}/${taskId}/labels`, payload);
}

async function updateTaskCmpLabel(accountId, id, objTask) {
  return await httpService.patch(`${BASE_URL}/${accountId}/${id}/cmp-label`, objTask);
}

async function deleteTaskCmpLabel(accountId, id, componentId) {
  return await httpService.delete(`${BASE_URL}/${accountId}/${id}/cmp-label/${componentId}`);
}

async function moveTasksAnotherBoard(accountId, tasks, boardId, groupId, oldBoardId, user) {
  const taskIds = (Array.isArray(tasks) ? tasks : [])
    .map(t => (typeof t === "string" ? t : t?._id))
    .filter(Boolean);

  return await httpService.patch(`${BASE_URL}/${accountId}/move-to-board`, {
    taskIds,
    boardId,
    groupId,
    oldBoardId,
    user
  });
}

async function getTasksByGroup(groupId, filters = {}) {
  const params = new URLSearchParams();

  if (filters.searchTitle) params.append('searchTitle', filters.searchTitle);
  if (filters.memberId) params.append('memberId', filters.memberId);
  if (filters.status) params.append('status', JSON.stringify(filters.status));
  if (filters.dynamic && Object.keys(filters.dynamic).length > 0) {
    params.append('dynamic', JSON.stringify(filters.dynamic));
  }

  const query = params.toString() ? `?${params.toString()}` : '';
  return await httpService.get(`${BASE_URL}/by-group/${groupId}${query}`);
}

async function getGroupTaskStats(groupId, filters = {}) {
  const params = new URLSearchParams();

  if (filters.searchTitle) params.append('searchTitle', filters.searchTitle);
  if (filters.memberId) params.append('memberId', filters.memberId);
  if (filters.status) params.append('status', JSON.stringify(filters.status));
  if (filters.dynamic && Object.keys(filters.dynamic).length > 0) {
    params.append('dynamic', JSON.stringify(filters.dynamic));
  }

  const query = params.toString() ? `?${params.toString()}` : '';
  return await httpService.get(`${BASE_URL}/stats/by-group/${groupId}${query}`);
}

async function archivedSubtasks(accountId, taskId, user) {
  return await httpService.patch(`${BASE_URL}/${accountId}/${taskId}/archivedSubtasks`, { user });
}

async function getTaskByAnalisys(accountId, userId, opts = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const url = `${BASE_URL}/${accountId}/user/${userId}/analysisTask?` + params.toString();
  return await httpService.get(url);
}

async function getTaskMention(accountId, userId, opts = {}) {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.offset) params.set('offset', String(opts.offset));
  const url = `${BASE_URL}/${accountId}/user/${userId}/tasksMentions?` + params.toString();
  return await httpService.get(url);
}

async function createTaskColumn(accountId, taskId, payload) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/columns`, payload);
}

async function reorderTaskColumns(accountId, taskId, payload) {
  return await httpService.patch(`${BASE_URL}/${accountId}/${taskId}/columns/reorder`, payload);
}

async function getTaskBpmnDiagram(accountId, taskId) {
  const res = await httpService.get(`${BASE_URL}/${accountId}/${taskId}/bpmn`);

  const xml =
    (typeof res?.content?.text === "string" && res.content.text) ||
    (typeof res?.xml === "string" && res.xml) ||
    "";

  return { ...res, xml };
}

async function listTaskDocuments(accountId, taskId, params = {}) {
  const qs = new URLSearchParams();

  if (params.type) qs.append("type", params.type);
  if (params.active === false) qs.append("active", "false");

  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await httpService.get(`${BASE_URL}/${accountId}/${taskId}/documents${query}`);

  return normalizeDocumentsResponse(res);
}

async function createTaskDocument(accountId, taskId, payload) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/documents`, payload);
}

async function getTaskDocumentById(accountId, taskId, documentId) {
  return await httpService.get(`${BASE_URL}/${accountId}/${taskId}/documents/${documentId}`);
}

async function updateTaskDocumentById(accountId, taskId, documentId, payload) {
  return await httpService.put(`${BASE_URL}/${accountId}/${taskId}/documents/${documentId}`, payload);
}

async function deleteTaskDocumentById(accountId, taskId, documentId) {
  return await httpService.delete(`${BASE_URL}/${accountId}/${taskId}/documents/${documentId}`);
}

async function saveTaskBpmnDiagram(accountId, taskId, xml) {
  const safeXml = typeof xml === "string" ? xml : "";
  return await httpService.put(`${BASE_URL}/${accountId}/${taskId}/bpmn`, {
    xml: safeXml,
    type: "bpmn",
    title: "Diagrama",
    content: { text: safeXml },
  });
}

async function createBelow(accountId, afterTaskId, payload) {
  const task = payload?.task || {};
  const activityData = payload?.activityData || null;

  return await httpService.post(`${BASE_URL}/${accountId}`, {
    task,
    activityData,
    insertAfterTaskId: afterTaskId,
  });
}

async function createComment(accountId, taskId, payload = {}) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/comments`, payload);
}

async function deleteComment(accountId, taskId, commentId, payload = {}) {
  return await httpService.delete(`${BASE_URL}/${accountId}/${taskId}/comments/${commentId}`, payload);
}

async function updateComment(accountId, taskId, commentId, payload = {}) {
  return await httpService.put(`${BASE_URL}/${accountId}/${taskId}/comments/${commentId}`, payload);
}

async function toggleLike(accountId, taskId, payload = {}) {
  return await httpService.post(`${BASE_URL}/${accountId}/${taskId}/likes`, payload);
}

async function createAnswer(accountId, taskId, commentId, payload = {}) {
  return await httpService.post(
    `${BASE_URL}/${accountId}/${taskId}/comments/${commentId}/answers`,
    payload
  );
}

async function deleteAnswer(accountId, taskId, commentId, answerId, payload = {}) {
  return await httpService.delete(
    `${BASE_URL}/${accountId}/${taskId}/comments/${commentId}/answers/${answerId}`,
    payload
  );
}

async function updateAnswer(accountId, taskId, commentId, answerId, payload = {}) {
  return await httpService.put(
    `${BASE_URL}/${accountId}/${taskId}/comments/${commentId}/answers/${answerId}`,
    payload
  );
}
